import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Database,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useSessionState } from "@/hooks/useSessionState";
import { useUrlState } from "@/hooks/useUrlState";
import { useAdminCronSchedule } from "@/hooks/useAdminCronSchedule";
import { useCapabilityOverrides } from "@/hooks/useCapabilities";
import {
  useActiveSourceLogos,
  useAvailableScrapers,
  usePatchSource,
  useSource,
  useSourceDetails,
  useSourceLogo,
  useSourceSitemapHealth,
  useSourcesCount,
  useSourcesList,
  useSourcesMapping,
  useSitemapHealthSummary,
  useUploadSourceLogo,
} from "@/hooks/useSources";
import {
  LOGO_VARIANTS,
  isLogoVariant,
  type LogoVariant,
  type SourceSitemapHealth,
} from "@/services/sourcesApi";
import type { SourcesSessionState, SourcesUrlState } from "@/types/pageState";
import { TripwireOverrideDialog } from "@/components/sources/TripwireOverrideDialog";
import { CronOverrideDialog } from "@/components/sources/CronOverrideDialog";
import { ThrottleConfigDialog } from "@/components/sources/ThrottleConfigDialog";
import {
  formatScraperCadence,
  formatThrottleSummary,
  isThrottleAtFloor,
  scraperConfigFromPartial,
} from "@/utils/scraperThrottle";
import {
  indexOverridesBySource,
  tripwireBadgeKind,
} from "@/utils/tripwireBadge";
import { cronBadgeKind } from "@/utils/cronBadge";
import { Toaster, toast } from "sonner";

const RELIABILITY_OPTIONS = [
  "all",
  "reliable",
  "degraded",
  "unreliable",
  "unknown",
] as const;

type ReliabilityValue = (typeof RELIABILITY_OPTIONS)[number];
type SortDirection = "asc" | "desc";
type SortKey =
  | "name"
  | "is_visible"
  | "scraper_available"
  | "reliability"
  | "trust_score"
  | "product_count"
  | "variant_count"
  | "checks_24h"
  | "errors_24h"
  | "last_scraped_at"
  | "last_checked_at";

const SORT_KEYS: readonly SortKey[] = [
  "name",
  "is_visible",
  "scraper_available",
  "reliability",
  "trust_score",
  "product_count",
  "variant_count",
  "checks_24h",
  "errors_24h",
  "last_scraped_at",
  "last_checked_at",
];

/**
 * `sort_key` is persisted in the URL and sessionStorage, so a stale session
 * from before "include_in_cron" was retired as a sort key would otherwise
 * cast through as SortKey and silently sort nothing (comparator falls to 0).
 */
function isSortKey(value: string): value is SortKey {
  return (SORT_KEYS as readonly string[]).includes(value);
}

interface SourceDashboardRow {
  id: string;
  name: string;
  homepage_url: string;
  logo: string | null;
  is_visible: boolean;
  product_count: number;
  variant_count: number;
  last_scraped_at: string | null;
  /** Latest TripwireCheck.checked_at for this source (sitemap health). */
  last_checked_at: string | null;
  mappedName?: string;
  scraper_available: boolean;
  scraper_name: string | null;
  scraper_base_url: string | null;
  reliability: string;
  checks_24h: number;
  errors_24h: number;
  /** Parsed 0–100 trust score, or null when absent. */
  trust_score: number | null;
  verified_review_count: number;
  unverified_review_count: number;
  external_review_count: number;
}

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const MAX_LOGO_UPLOAD_BYTES = 5 * 1024 * 1024;

function sortIcon(
  column: SortKey,
  activeKey: SortKey,
  direction: SortDirection,
) {
  if (activeKey !== column)
    return <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />;
  return direction === "asc" ? (
    <ArrowUp className="h-3.5 w-3.5" />
  ) : (
    <ArrowDown className="h-3.5 w-3.5" />
  );
}

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatPercent(value?: number | null) {
  if (value == null) return "—";
  return `${Math.round(value * 100)}%`;
}

function reliabilityVariant(
  reliability: string,
): "default" | "secondary" | "destructive" | "outline" {
  if (reliability === "reliable") return "default";
  if (reliability === "degraded") return "secondary";
  if (reliability === "unreliable") return "destructive";
  return "outline";
}

/** Trust scores are 0–100; Decimal may arrive as a string. */
function parseTrustScore(
  value: number | string | null | undefined,
): number | null {
  if (value == null) return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatTrustScore(score: number | null): string {
  if (score == null) return "—";
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

/** Color-code trust: green ≥70, amber ≥40, red below. */
function trustBadgeClass(score: number | null): string {
  if (score == null) return "";
  if (score >= 70) return "border-transparent bg-emerald-600 text-white";
  if (score >= 40) return "border-transparent bg-amber-500 text-white";
  return "border-transparent bg-red-600 text-white";
}

function dormantCronTitle(status: {
  environment: string;
  cron_enabled_override: boolean | null;
}) {
  const reason =
    status.cron_enabled_override === false
      ? "CRON_ENABLED is explicitly false for this deployment."
      : status.cron_enabled_override === null
        ? "CRON_ENABLED is unset, so non-production environments default to off unless you set CRON_ENABLED=true."
        : "Worker cron is off for this deployment.";
  return `Cron is allowed for this source, but the worker is not registering scheduled jobs in “${status.environment}”. ${reason} The capability override still applies if cron is turned on later.`;
}

function dormantTripwireTitle(status: {
  environment: string;
  cron_enabled_override: boolean | null;
}) {
  const reason =
    status.cron_enabled_override === false
      ? "CRON_ENABLED is explicitly false for this deployment."
      : status.cron_enabled_override === null
        ? "CRON_ENABLED is unset, so non-production environments default to off unless you set CRON_ENABLED=true."
        : "Worker cron is off for this deployment.";
  return `Tripwire is enabled for this source, but the worker is not registering scheduled jobs in “${status.environment}”. ${reason} Tripwire will start running again if cron is turned on later.`;
}

function CronStatusBadge({
  sourceId,
  cronQuery,
  overrides,
  overridesPending,
  overridesError,
}: {
  sourceId: string;
  cronQuery: ReturnType<typeof useAdminCronSchedule>;
  overrides: ReturnType<typeof indexOverridesBySource>;
  overridesPending: boolean;
  overridesError: boolean;
}) {
  const badge = cronBadgeKind({
    global: overrides.global,
    bySource: overrides.bySource,
    sourceId,
    cronEnabled: cronQuery.data?.is_cron_enabled,
    cronPending: cronQuery.isPending,
    overridesPending,
    overridesError,
  });

  if (badge.kind === "error") {
    return (
      <Badge variant="destructive" title={badge.title}>
        Error
      </Badge>
    );
  }

  if (badge.kind === "pending") {
    return (
      <Badge
        variant="outline"
        className="gap-1 font-normal text-muted-foreground"
        title={badge.title}
      >
        <Loader2 className="h-3 w-3 animate-spin shrink-0" aria-hidden />…
      </Badge>
    );
  }

  if (badge.kind === "off") {
    return (
      <Badge variant="secondary" title={badge.title}>
        Off
      </Badge>
    );
  }

  if (badge.kind === "dormant" && cronQuery.data) {
    return (
      <Badge
        variant="secondary"
        className="border-border bg-muted text-muted-foreground"
        title={dormantCronTitle(cronQuery.data)}
      >
        Dormant
      </Badge>
    );
  }

  return (
    <Badge variant="default" title={badge.title}>
      On
    </Badge>
  );
}

function TripwireStatusBadge({
  sourceId,
  cronQuery,
  absolute,
  scheduled,
  overridesPending,
  overridesError,
}: {
  sourceId: string;
  cronQuery: ReturnType<typeof useAdminCronSchedule>;
  absolute: ReturnType<typeof indexOverridesBySource>;
  scheduled: ReturnType<typeof indexOverridesBySource>;
  overridesPending: boolean;
  overridesError: boolean;
}) {
  const badge = tripwireBadgeKind({
    absoluteGlobal: absolute.global,
    absoluteBySource: absolute.bySource,
    scheduledGlobal: scheduled.global,
    scheduledBySource: scheduled.bySource,
    sourceId,
    cronEnabled: cronQuery.data?.is_cron_enabled,
    cronPending: cronQuery.isPending,
    overridesPending,
    overridesError,
  });

  if (badge.kind === "error") {
    return (
      <Badge variant="destructive" title={badge.title}>
        Error
      </Badge>
    );
  }

  if (badge.kind === "pending") {
    return (
      <Badge
        variant="outline"
        className="gap-1 font-normal text-muted-foreground"
        title={badge.title}
      >
        <Loader2 className="h-3 w-3 animate-spin shrink-0" aria-hidden />…
      </Badge>
    );
  }

  if (badge.kind === "killed") {
    return (
      <Badge variant="destructive" title={badge.title}>
        Killed
      </Badge>
    );
  }

  if (badge.kind === "off") {
    return (
      <Badge variant="secondary" title={badge.title}>
        Off
      </Badge>
    );
  }

  if (badge.kind === "dormant" && cronQuery.data) {
    return (
      <Badge
        variant="secondary"
        className="border-border bg-muted text-muted-foreground"
        title={dormantTripwireTitle(cronQuery.data)}
      >
        Dormant
      </Badge>
    );
  }

  return (
    <Badge variant="default" title={badge.title}>
      On
    </Badge>
  );
}

export default function Sources() {
  const [urlState, setUrlState] = useUrlState<SourcesUrlState>({
    schema: {
      search: "string",
      is_visible: "string",
      scraper_available: "string",
      reliability: "string",
      selected_source: "string",
      sort_key: "string",
      sort_direction: "string",
      page: "number",
      page_size: "number",
    },
    defaults: {
      is_visible: "all",
      scraper_available: "all",
      reliability: "all",
      sort_key: "name",
      sort_direction: "asc",
      page: 1,
      page_size: DEFAULT_PAGE_SIZE,
    },
    replace: true,
    debounceMs: 250,
  });
  const [sessionState, setSessionState] = useSessionState<SourcesSessionState>({
    key: "sources-ui",
    defaultValue: {
      autoRefreshEnabled: false,
      autoRefreshIntervalSec: 30,
      logoDialogOpen: false,
      logoDialogSourceId: null,
    },
  });

  const selectedSourceId = urlState.selected_source ?? "";
  const visibilityFilter = urlState.is_visible ?? "all";
  const scraperFilter = urlState.scraper_available ?? "all";
  const reliabilityFilter = (urlState.reliability ?? "all") as ReliabilityValue;
  const rawSortKey = urlState.sort_key ?? "name";
  const sortKey = isSortKey(rawSortKey) ? rawSortKey : "name";
  const sortDirection = (urlState.sort_direction ?? "asc") as SortDirection;
  const page = urlState.page ?? 1;
  const pageSize = urlState.page_size ?? DEFAULT_PAGE_SIZE;
  const searchFilter = (urlState.search ?? "").trim().toLowerCase();

  const sourcesQuery = useSourcesList(true);
  const countQuery = useSourcesCount();
  const mappingQuery = useSourcesMapping(true);
  const scrapersQuery = useAvailableScrapers();
  const activeLogosQuery = useActiveSourceLogos();
  const healthSummaryQuery = useSitemapHealthSummary();
  const cronScheduleQuery = useAdminCronSchedule();
  const tripwireAbsoluteQuery = useCapabilityOverrides("scraper.tripwire");
  const tripwireScheduledQuery = useCapabilityOverrides(
    "scraper.tripwire.scheduled",
  );
  const [tripwireDialogSourceId, setTripwireDialogSourceId] = useState<
    string | null
  >(null);
  const cronOverridesQuery = useCapabilityOverrides("scraper.cron");
  const [cronDialogSourceId, setCronDialogSourceId] = useState<string | null>(
    null,
  );
  const [throttleDialogOpen, setThrottleDialogOpen] = useState(false);

  const selectedSourceQuery = useSource(selectedSourceId);
  const selectedDetailsQuery = useSourceDetails(selectedSourceId);
  const selectedLogoQuery = useSourceLogo(selectedSourceId);
  const logoDialogLogoQuery = useSourceLogo(
    sessionState.logoDialogSourceId ?? "",
  );
  const selectedHealthQuery = useSourceSitemapHealth(selectedSourceId);
  const patchSourceMutation = usePatchSource();
  const uploadLogoMutation = useUploadSourceLogo();

  const isLoading =
    sourcesQuery.isLoading ||
    scrapersQuery.isLoading ||
    activeLogosQuery.isLoading ||
    healthSummaryQuery.isLoading;

  const logoBySource = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const logo of activeLogosQuery.data?.logos ?? []) {
      map.set(logo.source, logo.logos.default ?? null);
    }
    return map;
  }, [activeLogosQuery.data]);

  const scraperBySource = useMemo(() => {
    const map = new Map<
      string,
      {
        available: boolean;
        baseUrl: string;
        name: string;
      }
    >();
    for (const scraper of scrapersQuery.data?.scrapers ?? []) {
      map.set(scraper.source, {
        available: scraper.available,
        baseUrl: scraper.base_url,
        name: scraper.name,
      });
    }
    return map;
  }, [scrapersQuery.data]);

  const healthBySource = useMemo(() => {
    const map = new Map<string, SourceSitemapHealth>();
    for (const row of healthSummaryQuery.data?.sources ?? []) {
      map.set(row.source, row);
    }
    return map;
  }, [healthSummaryQuery.data]);

  const mapping = mappingQuery.data?.mapping ?? {};

  const tripwireAbsolute = useMemo(
    () => indexOverridesBySource(tripwireAbsoluteQuery.data),
    [tripwireAbsoluteQuery.data],
  );
  const tripwireScheduled = useMemo(
    () => indexOverridesBySource(tripwireScheduledQuery.data),
    [tripwireScheduledQuery.data],
  );
  const tripwireOverridesPending =
    (tripwireAbsoluteQuery.isPending &&
      tripwireAbsoluteQuery.data === undefined) ||
    (tripwireScheduledQuery.isPending &&
      tripwireScheduledQuery.data === undefined);
  const tripwireOverridesError =
    (tripwireAbsoluteQuery.isError &&
      tripwireAbsoluteQuery.data === undefined) ||
    (tripwireScheduledQuery.isError &&
      tripwireScheduledQuery.data === undefined);

  const cronOverrides = useMemo(
    () => indexOverridesBySource(cronOverridesQuery.data),
    [cronOverridesQuery.data],
  );
  const cronOverridesPending =
    cronOverridesQuery.isPending && cronOverridesQuery.data === undefined;
  const cronOverridesError =
    cronOverridesQuery.isError && cronOverridesQuery.data === undefined;

  const rows: SourceDashboardRow[] = useMemo(() => {
    const sourceRows = sourcesQuery.data?.sources ?? [];
    return sourceRows.map((source) => {
      const scraper = scraperBySource.get(source.id);
      const health = healthBySource.get(source.id);
      const mappedName = mapping[source.id];
      return {
        ...source,
        ...(mappedName ? { mappedName } : {}),
        logo: source.logos.default ?? logoBySource.get(source.id) ?? null,
        scraper_available: scraper?.available ?? false,
        scraper_name: scraper?.name ?? null,
        scraper_base_url: scraper?.baseUrl ?? null,
        reliability: health?.reliability_rating ?? "unknown",
        checks_24h: health?.total_checks_24h ?? 0,
        errors_24h: health?.errors_24h ?? 0,
        last_checked_at: health?.last_checked_at ?? null,
        trust_score: parseTrustScore(source.trust_score?.score),
        verified_review_count: source.trust_score?.verified_review_count ?? 0,
        unverified_review_count:
          source.trust_score?.unverified_review_count ?? 0,
        external_review_count: source.trust_score?.external_review_count ?? 0,
      };
    });
  }, [
    healthBySource,
    logoBySource,
    mapping,
    scraperBySource,
    sourcesQuery.data,
  ]);

  const [logoVariantInput, setLogoVariantInput] = useState<LogoVariant>("icon");
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const openLogoDialog = useCallback(
    (sourceId: string) => {
      setLogoVariantInput("icon");
      setSessionState((prev) => ({
        ...prev,
        logoDialogOpen: true,
        logoDialogSourceId: sourceId,
      }));
    },
    [setSessionState],
  );

  const closeLogoDialog = useCallback(() => {
    setSessionState((prev) => ({ ...prev, logoDialogOpen: false }));
    if (logoFileInputRef.current) logoFileInputRef.current.value = "";
  }, [setSessionState]);

  const handleUploadLogoFile = useCallback(async () => {
    const sourceId = sessionState.logoDialogSourceId;
    const file = logoFileInputRef.current?.files?.[0];
    const variant = logoVariantInput;
    if (!sourceId || !file) return;
    if (file.size > MAX_LOGO_UPLOAD_BYTES) {
      toast.error("Logo file must be 5 MB or smaller");
      return;
    }
    try {
      await uploadLogoMutation.mutateAsync({ sourceId, file, variant });
      toast.success(`Logo "${variant}" uploaded for ${sourceId}`);
      closeLogoDialog();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to upload logo",
      );
    }
  }, [
    closeLogoDialog,
    logoVariantInput,
    sessionState.logoDialogSourceId,
    uploadLogoMutation,
  ]);

  const filteredRows: SourceDashboardRow[] = useMemo(() => {
    return rows.filter((row: SourceDashboardRow) => {
      if (visibilityFilter === "visible" && !row.is_visible) return false;
      if (visibilityFilter === "hidden" && row.is_visible) return false;
      if (scraperFilter === "available" && !row.scraper_available) return false;
      if (scraperFilter === "unavailable" && row.scraper_available)
        return false;
      if (reliabilityFilter !== "all" && row.reliability !== reliabilityFilter)
        return false;

      if (!searchFilter) return true;
      return (
        row.id.toLowerCase().includes(searchFilter) ||
        row.name.toLowerCase().includes(searchFilter) ||
        row.homepage_url.toLowerCase().includes(searchFilter) ||
        (row.mappedName?.toLowerCase().includes(searchFilter) ?? false)
      );
    });
  }, [visibilityFilter, reliabilityFilter, rows, scraperFilter, searchFilter]);

  const sortedRows: SourceDashboardRow[] = useMemo(() => {
    const sorted = [...filteredRows].sort((a, b) => {
      if (sortKey === "name") return a.name.localeCompare(b.name);
      if (sortKey === "reliability")
        return a.reliability.localeCompare(b.reliability);
      if (sortKey === "trust_score")
        return (a.trust_score ?? -1) - (b.trust_score ?? -1);
      if (sortKey === "is_visible")
        return Number(a.is_visible) - Number(b.is_visible);
      if (sortKey === "scraper_available")
        return Number(a.scraper_available) - Number(b.scraper_available);
      if (sortKey === "product_count") return a.product_count - b.product_count;
      if (sortKey === "variant_count") return a.variant_count - b.variant_count;
      if (sortKey === "checks_24h") return a.checks_24h - b.checks_24h;
      if (sortKey === "errors_24h") return a.errors_24h - b.errors_24h;
      if (sortKey === "last_scraped_at") {
        const aTs = a.last_scraped_at
          ? new Date(a.last_scraped_at).getTime()
          : 0;
        const bTs = b.last_scraped_at
          ? new Date(b.last_scraped_at).getTime()
          : 0;
        return aTs - bTs;
      }
      if (sortKey === "last_checked_at") {
        const aTs = a.last_checked_at
          ? new Date(a.last_checked_at).getTime()
          : 0;
        const bTs = b.last_checked_at
          ? new Date(b.last_checked_at).getTime()
          : 0;
        return aTs - bTs;
      }
      return 0;
    });
    return sortDirection === "asc" ? sorted : sorted.reverse();
  }, [filteredRows, sortDirection, sortKey]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStartIndex = (currentPage - 1) * pageSize;
  const pagedRows = sortedRows.slice(pageStartIndex, pageStartIndex + pageSize);

  const visibleCount = rows.filter((row) => row.is_visible).length;
  const hiddenCount = rows.length - visibleCount;
  const scraperAvailableCount = rows.filter(
    (row) => row.scraper_available,
  ).length;
  const mappedCount = Object.keys(mapping).length;

  const selectedRow = rows.find(
    (row: SourceDashboardRow) => row.id === selectedSourceId,
  );
  const selectedSource = selectedSourceQuery.data;
  const selectedDetails = selectedDetailsQuery.data;
  const selectedScraperConfig = scraperConfigFromPartial(selectedDetails);
  const selectedHealth = selectedHealthQuery.data;
  const selectedLogo =
    selectedLogoQuery.data?.logos.default ??
    selectedRow?.logo ??
    selectedSource?.logos?.default ??
    null;

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      sourcesQuery.refetch(),
      countQuery.refetch(),
      mappingQuery.refetch(),
      scrapersQuery.refetch(),
      activeLogosQuery.refetch(),
      healthSummaryQuery.refetch(),
      cronScheduleQuery.refetch(),
      selectedSourceId ? selectedSourceQuery.refetch() : Promise.resolve(),
      selectedSourceId ? selectedDetailsQuery.refetch() : Promise.resolve(),
      selectedSourceId ? selectedLogoQuery.refetch() : Promise.resolve(),
      selectedSourceId ? selectedHealthQuery.refetch() : Promise.resolve(),
    ]);
  }, [
    activeLogosQuery,
    countQuery,
    cronScheduleQuery,
    healthSummaryQuery,
    mappingQuery,
    scrapersQuery,
    selectedDetailsQuery,
    selectedHealthQuery,
    selectedLogoQuery,
    selectedSourceId,
    selectedSourceQuery,
    sourcesQuery,
  ]);

  const handleSort = (column: SortKey) => {
    setUrlState((prev) => ({
      ...prev,
      sort_key: column,
      sort_direction:
        prev.sort_key === column && prev.sort_direction === "asc"
          ? "desc"
          : "asc",
      page: 1,
    }));
  };

  const handleToggleSourceVisible = async (
    sourceId: string,
    is_visible: boolean,
  ) => {
    try {
      await patchSourceMutation.mutateAsync({ sourceId, is_visible });
      toast.success(is_visible ? `Shown ${sourceId}` : `Hidden ${sourceId}`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update source",
      );
    }
  };

  useEffect(() => {
    if (!sessionState.autoRefreshEnabled) return;
    const timer = window.setInterval(
      () => {
        void handleRefresh();
      },
      Math.max(5, sessionState.autoRefreshIntervalSec) * 1000,
    );
    return () => window.clearInterval(timer);
  }, [
    handleRefresh,
    sessionState.autoRefreshEnabled,
    sessionState.autoRefreshIntervalSec,
  ]);

  return (
    <div className="space-y-6 p-6">
      <Toaster position="top-right" />
      <CronOverrideDialog
        open={cronDialogSourceId !== null}
        onOpenChange={(open) => {
          if (!open) setCronDialogSourceId(null);
        }}
        sourceId={cronDialogSourceId}
        sourceName={
          rows.find((row) => row.id === cronDialogSourceId)?.name ??
          cronDialogSourceId ??
          ""
        }
        cronRow={
          cronDialogSourceId
            ? cronOverridesQuery.data?.find(
                (row) =>
                  row.scope_type === "source" &&
                  row.scope_id === cronDialogSourceId,
              )
            : undefined
        }
        globalDeny={cronOverrides.global?.state === "deny"}
      />
      <ThrottleConfigDialog
        open={throttleDialogOpen}
        onOpenChange={setThrottleDialogOpen}
        sourceId={selectedSourceId || null}
        sourceName={selectedSource?.name ?? selectedSourceId}
        config={selectedDetails ? selectedScraperConfig : undefined}
      />
      <TripwireOverrideDialog
        open={tripwireDialogSourceId !== null}
        onOpenChange={(open) => {
          if (!open) setTripwireDialogSourceId(null);
        }}
        sourceId={tripwireDialogSourceId}
        sourceName={
          rows.find((row) => row.id === tripwireDialogSourceId)?.name ??
          tripwireDialogSourceId ??
          ""
        }
        absoluteRow={
          tripwireDialogSourceId
            ? tripwireAbsolute.bySource.get(tripwireDialogSourceId)
            : undefined
        }
        scheduledRow={
          tripwireDialogSourceId
            ? tripwireScheduled.bySource.get(tripwireDialogSourceId)
            : undefined
        }
        absoluteGlobalDeny={tripwireAbsolute.global?.state === "deny"}
      />
      <Dialog open={sessionState.logoDialogOpen} onOpenChange={closeLogoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage logo</DialogTitle>
            <DialogDescription>
              Upload a logo file for this source. Choose a variant name and
              select an image.
            </DialogDescription>
          </DialogHeader>

          <div className="text-sm">
            <span className="text-muted-foreground">Source:</span>{" "}
            <span className="font-mono">
              {sessionState.logoDialogSourceId ?? "—"}
            </span>
          </div>

          {logoDialogLogoQuery.data?.logos.variants &&
            Object.keys(logoDialogLogoQuery.data.logos.variants).length > 0 && (
              <div className="grid gap-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Current variants
                </p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(logoDialogLogoQuery.data.logos.variants).map(
                    ([name, url]) => (
                      <a
                        key={name}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs hover:bg-muted"
                      >
                        {name}
                        <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    ),
                  )}
                </div>
              </div>
            )}

          <div className="grid gap-2">
            <Label htmlFor="logo_variant">Variant name</Label>
            <Select
              value={logoVariantInput}
              onValueChange={(value) => {
                if (isLogoVariant(value)) setLogoVariantInput(value);
              }}
            >
              <SelectTrigger id="logo_variant">
                <SelectValue placeholder="Variant" />
              </SelectTrigger>
              <SelectContent>
                {LOGO_VARIANTS.map((variant) => (
                  <SelectItem key={variant} value={variant}>
                    {variant}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="logo_file">Upload image</Label>
            <p className="text-xs text-muted-foreground">
              PNG, JPEG, WebP, or GIF (max 5 MB). Uploads are converted to WebP
              and served from the CDN.
            </p>
            <div className="flex gap-2">
              <input
                ref={logoFileInputRef}
                id="logo_file"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="flex-1 text-sm file:mr-2 file:rounded file:border file:border-input file:bg-background file:px-3 file:py-1 file:text-sm"
                disabled={uploadLogoMutation.isPending}
              />
              <Button
                onClick={() => void handleUploadLogoFile()}
                disabled={uploadLogoMutation.isPending}
              >
                {uploadLogoMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Upload"
                )}
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeLogoDialog}
              disabled={uploadLogoMutation.isPending}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Sources</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Unified source dashboard with status, logos, scraper availability,
            and sitemap reliability.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={String(sessionState.autoRefreshIntervalSec)}
            onValueChange={(value) =>
              setSessionState((prev) => ({
                ...prev,
                autoRefreshIntervalSec: Number(value),
              }))
            }
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Auto-refresh" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="15">15 sec</SelectItem>
              <SelectItem value="30">30 sec</SelectItem>
              <SelectItem value="60">60 sec</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant={sessionState.autoRefreshEnabled ? "default" : "outline"}
            size="sm"
            onClick={() =>
              setSessionState((prev) => ({
                ...prev,
                autoRefreshEnabled: !prev.autoRefreshEnabled,
              }))
            }
          >
            Auto-refresh {sessionState.autoRefreshEnabled ? "On" : "Off"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleRefresh()}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Sources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">
              {countQuery.data?.count ?? rows.length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Visible
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{visibleCount}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Hidden: {hiddenCount}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Scrapers Available
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{scraperAvailableCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Reliable
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">
              {healthSummaryQuery.data?.reliable_count ?? 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Unreliable
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">
              {healthSummaryQuery.data?.unreliable_count ?? 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Mapped Labels
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">{mappedCount}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            <Input
              placeholder="Search source id, name, URL..."
              value={urlState.search ?? ""}
              onChange={(event) => {
                const rawValue = event.target.value;
                const shouldClear = rawValue.trim().length === 0;
                setUrlState((prev) => ({
                  ...prev,
                  search: shouldClear ? undefined : rawValue,
                  page: 1,
                }));
              }}
            />
            <Select
              value={visibilityFilter}
              onValueChange={(value) =>
                setUrlState((prev) => ({
                  ...prev,
                  is_visible: value,
                  page: 1,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Visibility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All visibility</SelectItem>
                <SelectItem value="visible">Visible</SelectItem>
                <SelectItem value="hidden">Hidden</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={scraperFilter}
              onValueChange={(value) =>
                setUrlState((prev) => ({
                  ...prev,
                  scraper_available: value,
                  page: 1,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Scraper availability" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All scraper states</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="unavailable">Unavailable</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={reliabilityFilter}
              onValueChange={(value) =>
                setUrlState((prev) => ({
                  ...prev,
                  reliability: value,
                  page: 1,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Reliability" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All reliability levels</SelectItem>
                <SelectItem value="reliable">Reliable</SelectItem>
                <SelectItem value="degraded">Degraded</SelectItem>
                <SelectItem value="unreliable">Unreliable</SelectItem>
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              className="justify-start"
              onClick={() =>
                setUrlState((prev) => ({
                  ...prev,
                  search: undefined,
                  is_visible: "all",
                  scraper_available: "all",
                  reliability: "all",
                  page: 1,
                }))
              }
            >
              Clear filters
            </Button>
            <Select
              value={String(pageSize)}
              onValueChange={(value) =>
                setUrlState((prev) => ({
                  ...prev,
                  page_size: Number(value),
                  page: 1,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Page size" />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size} / page
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Source Table ({sortedRows.length}) - page {currentPage} of{" "}
            {totalPages}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading sources...
            </div>
          ) : filteredRows.length === 0 ? (
            <p className="text-muted-foreground py-8">
              No sources matched your filters.
            </p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1"
                        onClick={() => handleSort("name")}
                      >
                        Source {sortIcon("name", sortKey, sortDirection)}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1"
                        onClick={() => handleSort("is_visible")}
                      >
                        Visibility{" "}
                        {sortIcon("is_visible", sortKey, sortDirection)}
                      </button>
                    </TableHead>
                    <TableHead className="w-[1%] whitespace-nowrap">
                      Cron
                    </TableHead>
                    <TableHead className="w-[1%] whitespace-nowrap">
                      Tripwire
                    </TableHead>
                    <TableHead>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1"
                        onClick={() => handleSort("scraper_available")}
                      >
                        Scraper{" "}
                        {sortIcon("scraper_available", sortKey, sortDirection)}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1"
                        onClick={() => handleSort("reliability")}
                      >
                        Reliability{" "}
                        {sortIcon("reliability", sortKey, sortDirection)}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1"
                        onClick={() => handleSort("trust_score")}
                      >
                        Trust {sortIcon("trust_score", sortKey, sortDirection)}
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1"
                        onClick={() => handleSort("product_count")}
                      >
                        Products{" "}
                        {sortIcon("product_count", sortKey, sortDirection)}
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1"
                        onClick={() => handleSort("variant_count")}
                      >
                        Variants{" "}
                        {sortIcon("variant_count", sortKey, sortDirection)}
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1"
                        onClick={() => handleSort("checks_24h")}
                      >
                        Checks (24h){" "}
                        {sortIcon("checks_24h", sortKey, sortDirection)}
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1"
                        onClick={() => handleSort("errors_24h")}
                      >
                        Errors (24h){" "}
                        {sortIcon("errors_24h", sortKey, sortDirection)}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1"
                        onClick={() => handleSort("last_scraped_at")}
                      >
                        Last Scraped{" "}
                        {sortIcon("last_scraped_at", sortKey, sortDirection)}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1"
                        onClick={() => handleSort("last_checked_at")}
                      >
                        Tripwire last checked{" "}
                        {sortIcon("last_checked_at", sortKey, sortDirection)}
                      </button>
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedRows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer"
                      onClick={() =>
                        setUrlState((prev) => ({
                          ...prev,
                          selected_source: row.id,
                        }))
                      }
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {row.logo ? (
                            <img
                              src={row.logo}
                              alt={`${row.name} logo`}
                              className="h-8 w-8 rounded border object-contain p-1 bg-background"
                              loading="lazy"
                            />
                          ) : (
                            <div className="h-8 w-8 rounded border bg-muted flex items-center justify-center">
                              <ImageIcon className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{row.name}</p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {row.id}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell
                        className="w-[1%] whitespace-nowrap"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={row.is_visible}
                            disabled={
                              patchSourceMutation.isPending &&
                              patchSourceMutation.variables?.sourceId === row.id
                            }
                            onCheckedChange={(checked) => {
                              if (checked === "indeterminate") return;
                              void handleToggleSourceVisible(
                                row.id,
                                Boolean(checked),
                              );
                            }}
                            aria-label={
                              row.is_visible
                                ? `Hide ${row.name}`
                                : `Show ${row.name}`
                            }
                          />
                          <Badge
                            variant={row.is_visible ? "default" : "secondary"}
                          >
                            {row.is_visible ? "Visible" : "Hidden"}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell
                        className="w-[1%] whitespace-nowrap"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <div className="flex items-center gap-2">
                          <CronStatusBadge
                            sourceId={row.id}
                            cronQuery={cronScheduleQuery}
                            overrides={cronOverrides}
                            overridesPending={cronOverridesPending}
                            overridesError={cronOverridesError}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setCronDialogSourceId(row.id)}
                          >
                            Manage
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell
                        className="w-[1%] whitespace-nowrap"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <div className="flex items-center gap-2">
                          <TripwireStatusBadge
                            sourceId={row.id}
                            cronQuery={cronScheduleQuery}
                            absolute={tripwireAbsolute}
                            scheduled={tripwireScheduled}
                            overridesPending={tripwireOverridesPending}
                            overridesError={tripwireOverridesError}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setTripwireDialogSourceId(row.id)}
                          >
                            Manage
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            row.scraper_available ? "default" : "outline"
                          }
                        >
                          {row.scraper_available ? "Available" : "Unavailable"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={reliabilityVariant(row.reliability)}>
                          {row.reliability}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {row.trust_score == null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <Badge
                            variant="outline"
                            className={trustBadgeClass(row.trust_score)}
                          >
                            {formatTrustScore(row.trust_score)}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.product_count}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.variant_count}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.checks_24h}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.errors_24h}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDateTime(row.last_scraped_at)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDateTime(row.last_checked_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              window.open(
                                row.homepage_url,
                                "_blank",
                                "noopener,noreferrer",
                              );
                            }}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <Pagination
            currentPage={currentPage}
            totalItems={sortedRows.length}
            itemsPerPage={pageSize}
            onPageChange={(nextPage) =>
              setUrlState((prev) => ({ ...prev, page: nextPage }))
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="w-4 h-4" />
            Source Details
            {selectedSourceId && (
              <Badge variant="outline">{selectedSourceId}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!selectedSourceId ? (
            <p className="text-muted-foreground">
              Select a source from the table to view detailed stats.
            </p>
          ) : selectedDetailsQuery.isLoading ||
            selectedSourceQuery.isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading source details...
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Identity
                </p>
                <div className="flex items-center gap-3">
                  {selectedLogo ? (
                    <img
                      src={selectedLogo}
                      alt={`${selectedSource?.name ?? selectedSourceId} logo`}
                      className="h-10 w-10 rounded border object-contain p-1 bg-background"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded border bg-muted flex items-center justify-center">
                      <ImageIcon className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium">
                      {selectedSource?.name ?? selectedSourceId}
                    </p>
                    <p className="text-xs text-muted-foreground font-mono">
                      {selectedSourceId}
                    </p>
                  </div>
                </div>
                {selectedSource?.homepage_url ? (
                  <div className="flex items-center gap-2">
                    <a
                      href={selectedSource.homepage_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-primary inline-flex items-center gap-1 hover:underline"
                    >
                      {selectedSource.homepage_url}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openLogoDialog(selectedSourceId)}
                    >
                      <ImageIcon className="w-3.5 h-3.5 mr-1" />
                      Manage Logo
                    </Button>
                  </div>
                ) : null}
              </div>

              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Catalog
                </p>
                <p className="text-sm">
                  Products: {selectedDetails?.product_count ?? 0}
                </p>
                <p className="text-sm">
                  Variants: {selectedDetails?.variant_count ?? 0}
                </p>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Visible:</span>
                  <Checkbox
                    checked={Boolean(selectedDetails?.is_visible)}
                    disabled={
                      !selectedDetails ||
                      (patchSourceMutation.isPending &&
                        patchSourceMutation.variables?.sourceId ===
                          selectedSourceId)
                    }
                    onCheckedChange={(checked) => {
                      if (checked === "indeterminate" || !selectedSourceId)
                        return;
                      void handleToggleSourceVisible(
                        selectedSourceId,
                        Boolean(checked),
                      );
                    }}
                    aria-label={
                      selectedDetails?.is_visible
                        ? `Hide ${selectedSource?.name ?? selectedSourceId}`
                        : `Show ${selectedSource?.name ?? selectedSourceId}`
                    }
                  />
                  <Badge
                    variant={
                      selectedDetails?.is_visible ? "default" : "secondary"
                    }
                  >
                    {selectedDetails?.is_visible ? "Yes" : "No"}
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Cron:</span>
                  {selectedSourceId ? (
                    <CronStatusBadge
                      sourceId={selectedSourceId}
                      cronQuery={cronScheduleQuery}
                      overrides={cronOverrides}
                      overridesPending={cronOverridesPending}
                      overridesError={cronOverridesError}
                    />
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!selectedSourceId}
                    onClick={() =>
                      selectedSourceId &&
                      setCronDialogSourceId(selectedSourceId)
                    }
                  >
                    Manage
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Tripwire:</span>
                  {selectedSourceId ? (
                    <TripwireStatusBadge
                      sourceId={selectedSourceId}
                      cronQuery={cronScheduleQuery}
                      absolute={tripwireAbsolute}
                      scheduled={tripwireScheduled}
                      overridesPending={tripwireOverridesPending}
                      overridesError={tripwireOverridesError}
                    />
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!selectedSourceId}
                    onClick={() =>
                      selectedSourceId &&
                      setTripwireDialogSourceId(selectedSourceId)
                    }
                  >
                    Manage
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Scraper
                </p>
                <p className="text-sm">
                  Availability:{" "}
                  <Badge
                    variant={
                      selectedRow?.scraper_available ? "default" : "outline"
                    }
                  >
                    {selectedRow?.scraper_available
                      ? "Available"
                      : "Unavailable"}
                  </Badge>
                </p>
                <p className="text-sm">
                  Platform:{" "}
                  {selectedDetails?.platform ?? selectedHealth?.platform ?? "—"}
                </p>
                <p className="text-sm text-muted-foreground">
                  Last scraped:{" "}
                  {formatDateTime(selectedDetails?.last_scraped_at)}
                </p>
                <p className="text-sm">
                  Cadence:{" "}
                  {formatScraperCadence(
                    selectedScraperConfig.scrape_interval_hours,
                  )}
                </p>
                <p className="text-sm">
                  Throttle: {formatThrottleSummary(selectedScraperConfig)}
                </p>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Auto-tune:</span>
                  <Badge
                    variant={
                      selectedScraperConfig.scraper_throttle_auto
                        ? "default"
                        : "secondary"
                    }
                  >
                    {selectedScraperConfig.scraper_throttle_auto
                      ? "On"
                      : "Pinned"}
                  </Badge>
                  <span className="text-muted-foreground">
                    {selectedScraperConfig.scraper_clean_run_streak} clean
                    {selectedScraperConfig.scraper_clean_run_streak === 1
                      ? " run"
                      : " runs"}
                  </span>
                  {isThrottleAtFloor(
                    selectedScraperConfig.scraper_batch_size,
                  ) ? (
                    <Badge variant="destructive">At floor</Badge>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!selectedSourceId}
                    onClick={() => setThrottleDialogOpen(true)}
                  >
                    Manage
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Sitemap Health
                </p>
                <p className="text-sm">
                  Reliability:{" "}
                  <Badge
                    variant={reliabilityVariant(
                      selectedHealth?.reliability_rating ?? "unknown",
                    )}
                  >
                    {selectedHealth?.reliability_rating ?? "unknown"}
                  </Badge>
                </p>
                <p className="text-sm">
                  Accuracy:{" "}
                  {formatPercent(selectedHealth?.latest_accuracy_score)}{" "}
                  (latest),{" "}
                  {formatPercent(selectedHealth?.avg_accuracy_score_30d)} (30d
                  avg)
                </p>
                <p className="text-sm text-muted-foreground">
                  Last check: {formatDateTime(selectedHealth?.last_checked_at)}
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Trust score
                </p>
                <p className="text-sm flex items-center gap-2">
                  Score:{" "}
                  {selectedRow?.trust_score == null ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <Badge
                      variant="outline"
                      className={trustBadgeClass(selectedRow.trust_score)}
                    >
                      {formatTrustScore(selectedRow.trust_score)}
                    </Badge>
                  )}
                </p>
                <p className="text-sm">
                  External reviews: {selectedRow?.external_review_count ?? 0}
                </p>
                <p className="text-sm">
                  Verified reviews: {selectedRow?.verified_review_count ?? 0}
                </p>
                <p className="text-sm">
                  Unverified reviews:{" "}
                  {selectedRow?.unverified_review_count ?? 0}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
